import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';
import { migrosLogoDataUrl } from './migros-logo-data';
export const migrosLogoAssetUrl: string = migrosLogoDataUrl;

// ─── Types (mirror FinalInspectionDetail) ──────────────────────────────────
interface ChecklistItem {
  id: string;
  title: string;
  category?: string | null;
  status?: 'pass' | 'fail' | 'na' | null;
  remarks?: string | null;
  evidence_images?: string[];
}

interface DefectItem {
  id: string;
  sku_id?: string | null;
  type?: string | null;
  title?: string | null;
  category?: string | null;
  severity?: string | null;
  level?: string | null;
  count?: number;
  location?: string | null;
  description?: string | null;
  evidence_images?: string[];
}

interface PhotoItem {
  id: string;
  sku_id?: string | null;
  tile_key?: string | null;
  label?: string | null;
  tile_label?: string | null;
  image_url: string;
  is_mandatory?: boolean;
  required?: boolean;
}

interface AuditItem {
  id: string;
  sku_id?: string | null;
  type: 'MEASUREMENT' | 'WEIGHT' | 'METAL_DETECTION';
  data?: any;
  result?: 'pass' | 'fail' | 'pending';
  notes?: string | null;
  remarks?: string | null;
}

interface SkuItem {
  id: string;
  po_sku_id?: string | null;
  sku_code?: string | null;
  sku_name?: string | null;
  description?: string | null;
  sku_description?: string | null;
  ordered_qty?: number;
  quantity?: number;
  inspected_qty?: number;
  result?: 'pass' | 'fail' | 'pending';
  colour?: string | null;
  color?: string | null;
  size?: string | null;
  vendor_style?: string | null;
  vendorStyle?: string | null;
  production_completion?: number;
  productionCompletion?: number;
  metal_detection?: string | null;
  metalDetection?: string | null;
  defects?: DefectItem[];
  photoTiles?: PhotoItem[];
  photo_tiles?: PhotoItem[];
  photos?: PhotoItem[];
}

export interface MigrosFinalInspectionPDFData {
  id: string;
  po_number?: string | null;
  customer_name?: string | null;
  product_category?: string | null;
  po_ship_date?: string | null;
  po_notes?: string | null;
  inspection_level?: string | null;
  aql_major?: string | null;
  aql_minor?: string | null;
  aql_critical?: string | null;
  lot_size?: number;
  remarks?: string | null;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED';
  overall_result: 'PASS' | 'FAIL' | 'PENDING';
  inspector_name?: string | null;
  inspector_signature_url?: string | null;
  factory_rep_signature_url?: string | null;
  created_at: string;
  inspector?: { id: string; name: string; profileImage?: string | null };
  checklists?: ChecklistItem[];
  defects?: DefectItem[];
  skus?: SkuItem[];
  photos?: PhotoItem[];
  audits?: AuditItem[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────
const NILL = 'Nill';

const formatDate = (raw?: string | null) => {
  if (!raw) return NILL;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '.');
};

const val = (v: any): string => {
  if (v === null || v === undefined) return NILL;
  const str = String(v).trim();
  return str.length === 0 ? NILL : str;
};

const getAuditImages = (auditData: any): string[] => {
  if (!auditData) return [];
  const arr = Array.isArray(auditData.imageUrls) ? auditData.imageUrls
    : Array.isArray(auditData.image_urls) ? auditData.image_urls
    : Array.isArray(auditData.evidence_images) ? auditData.evidence_images
    : [];
  return (arr as string[]).filter(Boolean);
};

// ─── Brand ──────────────────────────────────────────────────────────────────
const MIGROS_ORANGE = '#f2710d';
const BORDER = '#000000';

// ─── Styles ─────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#000000',
    backgroundColor: '#ffffff',
    paddingHorizontal: 32,
    paddingTop: 18,
    paddingBottom: 50,
  },

  // Fixed page header
  topHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  logoImg: { width: 140, height: 26, objectFit: 'contain' },
  subTitle: { fontSize: 8.5, color: '#000000', marginTop: 6, marginBottom: 10 },

  pageTitleLarge: { fontSize: 22, fontFamily: 'Helvetica-Bold', color: '#000000', marginTop: 8, marginBottom: 14, marginLeft: 10 },

  // Stage checkbox row
  stageRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 },

  // Product picture row
  pictureRow: { flexDirection: 'row', borderWidth: 1, borderColor: BORDER, marginBottom: 0 },
  pictureCell: { flex: 1, borderRightWidth: 1, borderRightColor: BORDER, padding: 4 },
  pictureCellLast: { flex: 1, padding: 4 },
  pictureHeader: { backgroundColor: '#ffffff', padding: 3, fontSize: 9, fontFamily: 'Helvetica-Bold' },
  pictureImg: { width: '100%', height: 150, objectFit: 'contain' },
  pictureEmpty: { width: '100%', height: 150, alignItems: 'center', justifyContent: 'center' },
  pictureEmptyText: { fontSize: 9, color: '#888888', fontStyle: 'italic' },

  // Standard info table (label | value)
  infoTable: { borderWidth: 1, borderColor: BORDER, borderTopWidth: 0 },
  infoRow: { flexDirection: 'row', borderTopWidth: 1, borderTopColor: BORDER, minHeight: 20 },
  infoLabelCell: { width: 180, padding: 4, paddingHorizontal: 6, borderRightWidth: 1, borderRightColor: BORDER, justifyContent: 'center' },
  infoLabelText: { fontSize: 9 },
  infoValueCell: { flex: 1, padding: 4, paddingHorizontal: 6, justifyContent: 'center' },
  infoValueText: { fontSize: 9, fontFamily: 'Helvetica-Bold' },

  // Two-value info row (e.g. check-in / check-out)
  infoValueCellSplit: { flex: 1, padding: 4, paddingHorizontal: 6, justifyContent: 'center', borderRightWidth: 1, borderRightColor: BORDER },

  // LOT Quantity matrix inside infoValueCell
  matrixWrap: { flex: 1 },
  matrixRow: { flexDirection: 'row', minHeight: 16 },
  matrixCellLabel: { width: 38, padding: 2, borderRightWidth: 0.5, borderRightColor: BORDER, justifyContent: 'center', alignItems: 'center' },
  matrixCell: { flex: 1, padding: 2, borderRightWidth: 0.5, borderRightColor: BORDER, justifyContent: 'center', alignItems: 'center' },
  matrixCellLast: { flex: 1, padding: 2, justifyContent: 'center', alignItems: 'center' },
  matrixCellTotal: { width: 60, padding: 2, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f5f5f5' },
  matrixText: { fontSize: 8.5 },
  matrixTextBold: { fontSize: 8.5, fontFamily: 'Helvetica-Bold' },
  matrixDivider: { borderTopWidth: 0.5, borderTopColor: BORDER },

  // Inspection Result row (header row)
  resultHeaderRow: { flexDirection: 'row', borderWidth: 1, borderColor: BORDER, marginTop: 14, marginBottom: 0 },
  resultHeaderLabel: { flex: 1, padding: 4, paddingHorizontal: 6, borderRightWidth: 1, borderRightColor: BORDER, justifyContent: 'center' },
  resultHeaderLabelText: { fontSize: 9, fontFamily: 'Helvetica-Bold' },
  resultHeaderCheckCell: { flex: 1, padding: 4, paddingHorizontal: 6, borderRightWidth: 1, borderRightColor: BORDER, justifyContent: 'center' },
  resultHeaderCheckCellLast: { flex: 1, padding: 4, paddingHorizontal: 6, justifyContent: 'center' },

  // Parts table (Conformed / Not Conformed)
  partRow: { flexDirection: 'row', borderWidth: 1, borderColor: BORDER, borderTopWidth: 0 },
  partLabelCell: { flex: 1, padding: 4, paddingHorizontal: 6, borderRightWidth: 1, borderRightColor: BORDER, justifyContent: 'center' },
  partCheckCell: { flex: 1, padding: 4, paddingHorizontal: 6, borderRightWidth: 1, borderRightColor: BORDER, justifyContent: 'center' },
  partCheckCellLast: { flex: 1, padding: 4, paddingHorizontal: 6, justifyContent: 'center' },

  // Signature blocks
  sigRow: { flexDirection: 'row', borderWidth: 1, borderColor: BORDER, marginTop: 14 },
  sigCell: { flex: 1, padding: 6, borderRightWidth: 1, borderRightColor: BORDER, minHeight: 70 },
  sigCellLast: { flex: 1, padding: 6, minHeight: 70 },
  sigTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 4 },
  sigField: { fontSize: 8.5, marginBottom: 2 },
  sigImage: { width: 90, height: 32, objectFit: 'contain', marginTop: 2 },

  // Section title (bold black, underlined-feeling)
  sectionTitle: { fontSize: 10, fontFamily: 'Helvetica-Bold', marginTop: 12, marginBottom: 6, textDecoration: 'underline' },

  // Generic data table
  dataTable: { borderWidth: 1, borderColor: BORDER, marginBottom: 6 },
  dataHeadRow: { flexDirection: 'row', backgroundColor: '#f0f0f0', borderBottomWidth: 1, borderBottomColor: BORDER },
  dataBodyRow: { flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: BORDER, minHeight: 20 },
  dataCell: { flex: 1, padding: 4, paddingHorizontal: 5, borderRightWidth: 0.5, borderRightColor: BORDER, justifyContent: 'center' },
  dataCellLast: { flex: 1, padding: 4, paddingHorizontal: 5, justifyContent: 'center' },
  dataCellText: { fontSize: 8.5 },
  dataCellTextBold: { fontSize: 8.5, fontFamily: 'Helvetica-Bold' },
  dataCellTextCenter: { fontSize: 8.5, textAlign: 'center' },
  dataCellTextHead: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', textAlign: 'center' },

  // Checkbox row collection
  checkboxRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 14, marginBottom: 6 },
  checkboxBox: { width: 9, height: 9, borderWidth: 0.7, borderColor: BORDER, alignItems: 'center', justifyContent: 'center' },
  checkboxBoxLg: { width: 11, height: 11, borderWidth: 0.7, borderColor: BORDER, alignItems: 'center', justifyContent: 'center' },
  checkboxMark: { fontSize: 7, fontFamily: 'Helvetica-Bold', lineHeight: 1 },
  checkboxMarkLg: { fontSize: 9, fontFamily: 'Helvetica-Bold', lineHeight: 1 },
  checkboxLabel: { fontSize: 9, marginLeft: 4 },
  checkboxItem: { flexDirection: 'row', alignItems: 'center', marginRight: 8 },

  // Picture summary list
  picList: { marginLeft: 14, marginTop: 4 },
  picListItem: { flexDirection: 'row', marginBottom: 2 },
  picListNum: { width: 18, fontSize: 9 },
  picListText: { fontSize: 9 },

  // Photos grid
  photoGrid: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 10 },
  photoTile: { width: 162 },
  photoImg: { width: 162, height: 180, objectFit: 'cover' },
  photoCaption: { fontSize: 7.5, color: '#444', marginTop: 2 },

  // Big section header
  bigSectionTitle: { fontSize: 22, fontFamily: 'Helvetica-Bold', color: '#000000', marginVertical: 12, marginLeft: 18 },

  // Footer
  pageFooter: {
    position: 'absolute',
    bottom: 14,
    left: 32,
    right: 32,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 0.5,
    borderTopColor: '#cccccc',
    paddingTop: 4,
  },
  footerLeft: { fontSize: 7.5, color: '#555555', flex: 1 },
  footerCenter: { fontSize: 7.5, color: '#555555', textAlign: 'center', flex: 1 },
  footerRight: { fontSize: 7.5, color: '#555555', textAlign: 'right', flex: 1 },

  // Remarks block
  remarksBlock: { borderWidth: 1, borderColor: BORDER, padding: 6, minHeight: 26, marginBottom: 6 },
  remarksLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 4 },
});

// ─── Sub-components ─────────────────────────────────────────────────────────
const PageTop = ({ logoSrc }: { logoSrc: string }) => (
  <>
    <View style={s.topHeader} fixed>
      <Image src={logoSrc} style={s.logoImg} />
    </View>
    <Text style={s.subTitle} fixed>FINALRANDOMINSPECTION(TEXTILE/HOMETEXTILE)Version-1</Text>
  </>
);

const PageFooter = () => (
  <View style={s.pageFooter} fixed>
    <Text style={s.footerLeft}>MIGROS Asia</Text>
    <Text
      style={s.footerCenter}
      render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
    />
    <Text style={s.footerRight}>Final Random Inspection</Text>
  </View>
);

const Checkbox = ({ checked, label, lg }: { checked?: boolean; label: string; lg?: boolean }) => (
  <View style={s.checkboxItem}>
    <View style={lg ? s.checkboxBoxLg : s.checkboxBox}>
      {checked ? <Text style={lg ? s.checkboxMarkLg : s.checkboxMark}>X</Text> : null}
    </View>
    <Text style={s.checkboxLabel}>{label}</Text>
  </View>
);

const InfoRow = ({ label, value, bold = true }: { label: string; value: React.ReactNode; bold?: boolean }) => (
  <View style={s.infoRow}>
    <View style={s.infoLabelCell}>
      <Text style={s.infoLabelText}>{label}</Text>
    </View>
    <View style={s.infoValueCell}>
      {typeof value === 'string' || typeof value === 'number'
        ? <Text style={bold ? s.infoValueText : { fontSize: 9 }}>{String(value)}</Text>
        : value}
    </View>
  </View>
);

const InfoSplitRow = ({ label, left, right }: { label: string; left: string; right: string }) => (
  <View style={s.infoRow}>
    <View style={s.infoLabelCell}>
      <Text style={s.infoLabelText}>{label}</Text>
    </View>
    <View style={s.infoValueCellSplit}>
      <Text style={s.infoValueText}>{left}</Text>
    </View>
    <View style={s.infoValueCell}>
      <Text style={s.infoValueText}>{right}</Text>
    </View>
  </View>
);

// ─── Helpers for content ────────────────────────────────────────────────────
const collectSizes = (skus: SkuItem[]): string[] => {
  const sizeMap = new Map<string, number>();
  skus.forEach(sku => {
    const sz = (sku.size || '').trim();
    if (sz) sizeMap.set(sz, (sizeMap.get(sz) || 0) + (sku.quantity ?? sku.ordered_qty ?? 0));
  });
  return Array.from(sizeMap.keys());
};

const sumQty = (skus: SkuItem[]) =>
  skus.reduce((acc, sku) => acc + (sku.quantity ?? sku.ordered_qty ?? 0), 0);

// Use the SKU's "photoTiles" mandatory cover front/back photos when available
const findCoverPhotos = (inspection: MigrosFinalInspectionPDFData) => {
  const allPhotos: PhotoItem[] = [];
  for (const p of inspection.photos || []) {
    if (p.image_url) allPhotos.push(p);
  }
  for (const sku of inspection.skus || []) {
    for (const arr of [sku.photoTiles, sku.photo_tiles, sku.photos]) {
      for (const p of arr || []) if (p.image_url) allPhotos.push(p);
    }
  }

  const findByKey = (...keys: string[]) =>
    allPhotos.find(p =>
      keys.some(k => {
        const tk = (p.tile_key || '').toLowerCase();
        const lb = (p.label || p.tile_label || '').toLowerCase();
        return tk.includes(k) || lb.includes(k);
      }),
    );

  const front = findByKey('front', 'product_front');
  const back = findByKey('back', 'product_back');
  return {
    front: front?.image_url || (allPhotos[0]?.image_url ?? null),
    back: back?.image_url || (allPhotos[1]?.image_url ?? null),
  };
};

// ─── Main PDF Document ──────────────────────────────────────────────────────
const MigrosFinalInspectionReportPDF = ({ inspection, logoDataUrl }: { inspection: MigrosFinalInspectionPDFData; logoDataUrl?: string }) => {
  const logoSrc = logoDataUrl || migrosLogoDataUrl;
  const result = inspection.overall_result || 'PENDING';
  const isPass = result === 'PASS';
  const isFail = result === 'FAIL';
  const inspectorName = inspection.inspector?.name || inspection.inspector_name || NILL;
  const skus = inspection.skus || [];
  const firstSku = skus[0];
  const globalDefects = inspection.defects || [];
  const globalPhotos = inspection.photos || [];
  const globalAudits = inspection.audits || [];
  const checklists = inspection.checklists || [];

  const totalOrderQty = sumQty(skus);
  const sizes = collectSizes(skus);
  const articleNo = val(firstSku?.sku_code || firstSku?.sku_name);

  const { front: frontPhoto, back: backPhoto } = findCoverPhotos(inspection);

  const majorDefects = globalDefects.filter(d => (d.level || d.severity || '').toLowerCase() === 'major');
  const minorDefects = globalDefects.filter(d => (d.level || d.severity || '').toLowerCase() === 'minor');
  const criticalDefects = globalDefects.filter(d => (d.level || d.severity || '').toLowerCase() === 'critical');
  const majorCount = majorDefects.reduce((acc, d) => acc + (d.count || 1), 0);
  const minorCount = minorDefects.reduce((acc, d) => acc + (d.count || 1), 0);
  const criticalCount = criticalDefects.reduce((acc, d) => acc + (d.count || 1), 0);

  // ── Page 1: LOT Quantity matrix ───────────────────────────────────────────
  const renderLotMatrix = () => {
    if (skus.length === 0 || sizes.length === 0) {
      return <Text style={s.matrixText}>{NILL}</Text>;
    }
    // Build per-size totals
    const qtyBySize = new Map<string, number>();
    skus.forEach(sku => {
      const sz = (sku.size || '').trim();
      if (sz) qtyBySize.set(sz, (qtyBySize.get(sz) || 0) + (sku.quantity ?? sku.ordered_qty ?? 0));
    });
    return (
      <View style={s.matrixWrap}>
        <View style={s.matrixRow}>
          <View style={s.matrixCellLabel}><Text style={s.matrixTextBold}>Size</Text></View>
          {sizes.map((sz, i) => (
            <View key={sz} style={i === sizes.length - 1 ? s.matrixCellLast : s.matrixCell}>
              <Text style={s.matrixTextBold}>{sz}</Text>
            </View>
          ))}
          <View style={s.matrixCellTotal}><Text style={s.matrixTextBold}>Total</Text></View>
        </View>
        <View style={[s.matrixRow, s.matrixDivider]}>
          <View style={s.matrixCellLabel}><Text style={s.matrixTextBold}>QTY</Text></View>
          {sizes.map((sz, i) => (
            <View key={sz} style={i === sizes.length - 1 ? s.matrixCellLast : s.matrixCell}>
              <Text style={s.matrixText}>{qtyBySize.get(sz) || 0}</Text>
            </View>
          ))}
          <View style={s.matrixCellTotal}>
            <Text style={s.matrixTextBold}>{totalOrderQty} PCS</Text>
          </View>
        </View>
      </View>
    );
  };

  // ── Page 2: Part 1 defects (colour/material/style) ────────────────────────
  // We don't have explicit category mapping → show all if no defects: Nill
  const part1Defects = globalDefects.filter(d => {
    const cat = (d.category || '').toLowerCase();
    return cat.includes('colour') || cat.includes('color') || cat.includes('material') || cat.includes('style') || cat.includes('print');
  });

  return (
    <Document title={`Migros Final Inspection Report - ${inspection.po_number || inspection.id}`}>

      {/* ═══ PAGE 1 — COVER ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop logoSrc={logoSrc} />

        {/* Stage checkbox row */}
        <View style={s.stageRow}>
          <Checkbox label="Factory Self Inspection" lg />
          <Checkbox label="1st Inspection" lg />
          <Checkbox label="2 nd Inspection" lg />
        </View>

        {/* Product Front / Back picture row */}
        <View style={s.pictureRow}>
          <View style={s.pictureCell}>
            <Text style={s.pictureHeader}>PRODUCAT FRONT PICTURE</Text>
            {frontPhoto ? (
              <Image src={frontPhoto} style={s.pictureImg} />
            ) : (
              <View style={s.pictureEmpty}>
                <Text style={s.pictureEmptyText}>{NILL}</Text>
              </View>
            )}
          </View>
          <View style={s.pictureCellLast}>
            <Text style={s.pictureHeader}>PRODUCAT BACK PICTURE</Text>
            {backPhoto ? (
              <Image src={backPhoto} style={s.pictureImg} />
            ) : (
              <View style={s.pictureEmpty}>
                <Text style={s.pictureEmptyText}>{NILL}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Info table — wrap each row in a bordered container so top border lines up */}
        <View style={{ borderWidth: 1, borderColor: BORDER, borderTopWidth: 0 }}>
          <InfoRow label="Inspection Date:" value={formatDate(inspection.created_at)} />

          <InfoSplitRow
            label="Check-in and Check-out Time:"
            left={NILL}
            right={NILL}
          />

          <InfoRow label="SAP No./Supplier Name:" value={val(inspection.customer_name)} />
          <InfoRow label="Factory Name& ID:" value={NILL} />
          <InfoRow label="Factory Address:" value={NILL} />
          <InfoRow label="Contract/ Order no.:" value={val(inspection.po_number)} />
          <InfoRow label="Product Description:" value={val(inspection.product_category || inspection.po_notes)} />
          <InfoRow label="Article No.:" value={articleNo} />
          <InfoRow label="Order Quantity:" value={`${totalOrderQty} PCS`} />
          <InfoRow label="LOT Packing (YES/NO):" value={NILL} />

          {/* LOT Quantity (matrix) */}
          <View style={s.infoRow}>
            <View style={s.infoLabelCell}>
              <Text style={s.infoLabelText}>LOT Quantity:</Text>
            </View>
            <View style={[s.infoValueCell, { padding: 0 }]}>
              {renderLotMatrix()}
            </View>
          </View>

          {/* Subsequent Quantity (no data) */}
          <View style={s.infoRow}>
            <View style={s.infoLabelCell}>
              <Text style={s.infoLabelText}>Subsequent Quantity:</Text>
            </View>
            <View style={[s.infoValueCell, { padding: 4 }]}>
              <Text style={s.infoValueText}>{NILL}</Text>
            </View>
          </View>

          <InfoRow label="Reference/ Shipment sample seal date:" value={NILL} />
        </View>

        {/* Inspection Result Pass/Fail row */}
        <View style={s.resultHeaderRow}>
          <View style={s.resultHeaderLabel}>
            <Text style={s.resultHeaderLabelText}>INSPECTIONRESULT</Text>
          </View>
          <View style={s.resultHeaderCheckCell}>
            <Checkbox checked={isPass} label="PASSED" lg />
          </View>
          <View style={s.resultHeaderCheckCellLast}>
            <Checkbox checked={isFail} label="FAILED" lg />
          </View>
        </View>

        {/* Parts 1-5 */}
        {[
          'Part1-Colour,material and style',
          'Part2- Appearance, functioning and workmanship',
          'Part3-Data measurement and field tests',
          'Part4-Packaging and shipment quantity',
          'Part5-Minimum requirements for inspection',
        ].map(label => (
          <View key={label} style={s.partRow}>
            <View style={s.partLabelCell}>
              <Text style={{ fontSize: 9 }}>{label}</Text>
            </View>
            <View style={s.partCheckCell}>
              <Checkbox label="Conformed" />
            </View>
            <View style={s.partCheckCellLast}>
              <Checkbox label="Not Conformed" />
            </View>
          </View>
        ))}

        {/* Inspector / Factory Rep signature blocks */}
        <View style={s.sigRow}>
          <View style={s.sigCell}>
            <Text style={s.sigTitle}>INSPECTOR</Text>
            <Text style={s.sigField}>Name: {inspectorName}</Text>
            <Text style={s.sigField}>Signature:</Text>
            {inspection.inspector_signature_url ? (
              <Image src={inspection.inspector_signature_url} style={s.sigImage} />
            ) : null}
          </View>
          <View style={s.sigCellLast}>
            <Text style={s.sigTitle}>FACTORY REPRESENTATIVE</Text>
            <Text style={s.sigField}>Name:</Text>
            <Text style={s.sigField}>Signature:</Text>
            {inspection.factory_rep_signature_url ? (
              <Image src={inspection.factory_rep_signature_url} style={s.sigImage} />
            ) : null}
          </View>
        </View>

        <PageFooter />
      </Page>

      {/* ═══ PAGE 2 — PPL/FRL Test + Part 1 + Part 2 ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop logoSrc={logoSrc} />

        {/* PPL/FRL Test */}
        <View style={s.checkboxRow}>
          <Checkbox label="PPL/FRL Test" />
        </View>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Article No.</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>PPL/FRL report no.</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Laboratory for Testing</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Result(pass/fail)</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Remarks</Text></View>
          </View>
          <View style={s.dataBodyRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{articleNo}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
          </View>
        </View>

        {/* Part 1 - Colour/Material/Style */}
        <Text style={s.sectionTitle}>Part1-Colour,materialandstyle</Text>
        <View style={s.checkboxRow}>
          <Checkbox label="Conformed to Reference / Shipment Seal Sample" />
          <Checkbox label="Conformed to Printing / Color Swatches" />
        </View>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Defect Code</Text></View>
            <View style={[s.dataCellLast, { flex: 2 }]}><Text style={s.dataCellTextHead}>Defect Description</Text></View>
          </View>
          {part1Defects.length > 0 ? (
            part1Defects.map((d, i) => (
              <View key={d.id || i} style={s.dataBodyRow}>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{d.type || (i + 1).toString()}</Text></View>
                <View style={[s.dataCellLast, { flex: 2 }]}><Text style={s.dataCellText}>{d.title || d.description || NILL}</Text></View>
              </View>
            ))
          ) : (
            <View style={s.dataBodyRow}>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={[s.dataCellLast, { flex: 2 }]}><Text style={s.dataCellTextCenter}>NO DEFECT DETECTED</Text></View>
            </View>
          )}
        </View>
        <View style={s.remarksBlock}>
          <Text style={s.remarksLabel}>Remarks:</Text>
          <Text style={{ fontSize: 9 }}>{val(inspection.remarks)}</Text>
        </View>

        {/* Part 2 - Appearance */}
        <Text style={s.sectionTitle}>Part2- Appearance, functioning and workmanship</Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Inspection Level</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Sample Size</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Critical</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Major</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Minor</Text></View>
          </View>
          <View style={s.dataBodyRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(inspection.inspection_level)}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(inspection.lot_size)}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(inspection.aql_critical) === NILL ? '0' : inspection.aql_critical}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(inspection.aql_major)}</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{val(inspection.aql_minor)}</Text></View>
          </View>
        </View>

        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={[s.dataCell, { flex: 0.9 }]}><Text style={s.dataCellTextHead}>Defect Code</Text></View>
            <View style={[s.dataCell, { flex: 2.5 }]}><Text style={s.dataCellTextHead}>Defect Description</Text></View>
            <View style={[s.dataCell, { flex: 0.6 }]}><Text style={s.dataCellTextHead}>Cri</Text></View>
            <View style={[s.dataCell, { flex: 0.6 }]}><Text style={s.dataCellTextHead}>Maj</Text></View>
            <View style={[s.dataCellLast, { flex: 0.6 }]}><Text style={s.dataCellTextHead}>Min</Text></View>
          </View>
          {globalDefects.length > 0 ? (
            globalDefects.map((d, i) => {
              const lvl = (d.level || d.severity || '').toLowerCase();
              const c = d.count || 1;
              return (
                <View key={d.id || i} style={s.dataBodyRow}>
                  <View style={[s.dataCell, { flex: 0.9 }]}><Text style={s.dataCellTextCenter}>{d.type || (i + 1).toString()}</Text></View>
                  <View style={[s.dataCell, { flex: 2.5 }]}><Text style={s.dataCellText}>{d.title || d.description || NILL}</Text></View>
                  <View style={[s.dataCell, { flex: 0.6 }]}><Text style={s.dataCellTextCenter}>{lvl === 'critical' ? String(c).padStart(2, '0') : ''}</Text></View>
                  <View style={[s.dataCell, { flex: 0.6 }]}><Text style={s.dataCellTextCenter}>{lvl === 'major' ? String(c).padStart(2, '0') : ''}</Text></View>
                  <View style={[s.dataCellLast, { flex: 0.6 }]}><Text style={s.dataCellTextCenter}>{lvl === 'minor' ? String(c).padStart(2, '0') : ''}</Text></View>
                </View>
              );
            })
          ) : (
            <View style={s.dataBodyRow}>
              <View style={[s.dataCell, { flex: 0.9 }]}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={[s.dataCell, { flex: 2.5 }]}><Text style={s.dataCellTextCenter}>NO DEFECT DETECTED</Text></View>
              <View style={[s.dataCell, { flex: 0.6 }]}><Text style={s.dataCellTextCenter}>00</Text></View>
              <View style={[s.dataCell, { flex: 0.6 }]}><Text style={s.dataCellTextCenter}>00</Text></View>
              <View style={[s.dataCellLast, { flex: 0.6 }]}><Text style={s.dataCellTextCenter}>00</Text></View>
            </View>
          )}
          {/* Totals rows */}
          <View style={[s.dataBodyRow, { backgroundColor: '#f5f5f5' }]}>
            <View style={[s.dataCell, { flex: 3.4 }]}><Text style={[s.dataCellTextBold, { textAlign: 'right' }]}>No. of Defects Found</Text></View>
            <View style={[s.dataCell, { flex: 0.6 }]}><Text style={s.dataCellTextCenter}>{String(criticalCount).padStart(2, '0')}</Text></View>
            <View style={[s.dataCell, { flex: 0.6 }]}><Text style={s.dataCellTextCenter}>{String(majorCount).padStart(2, '0')}</Text></View>
            <View style={[s.dataCellLast, { flex: 0.6 }]}><Text style={s.dataCellTextCenter}>{String(minorCount).padStart(2, '0')}</Text></View>
          </View>
          <View style={[s.dataBodyRow, { backgroundColor: '#f5f5f5' }]}>
            <View style={[s.dataCell, { flex: 3.4 }]}><Text style={[s.dataCellTextBold, { textAlign: 'right' }]}>No. of Defects Allowed</Text></View>
            <View style={[s.dataCell, { flex: 0.6 }]}><Text style={s.dataCellTextCenter}>{val(inspection.aql_critical) === NILL ? '00' : inspection.aql_critical}</Text></View>
            <View style={[s.dataCell, { flex: 0.6 }]}><Text style={s.dataCellTextCenter}>{val(inspection.aql_major)}</Text></View>
            <View style={[s.dataCellLast, { flex: 0.6 }]}><Text style={s.dataCellTextCenter}>{val(inspection.aql_minor)}</Text></View>
          </View>
        </View>
        <View style={s.remarksBlock}>
          <Text style={s.remarksLabel}>Informative Remarks:</Text>
          <Text style={{ fontSize: 9 }}>{val(inspection.remarks)}</Text>
        </View>

        <PageFooter />
      </Page>

      {/* ═══ PAGE 3 — Part 3 + Part 4 + Part 5 ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop logoSrc={logoSrc} />

        {/* Part 3 - Data measurement and field tests */}
        <Text style={s.sectionTitle}>Part3- Data measurement and field tests (see supplementary data sheet for details)</Text>
        <View style={s.checkboxRow}>
          <Checkbox label="3.1MeasurementCheck" />
          <Checkbox label="3.2FunctionCheck" />
          <Checkbox label="3.3Humiditycheck" />
          <Checkbox label="3.4DropTest" />
        </View>
        <View style={s.checkboxRow}>
          <Checkbox label="3.5 Others (METALDETECTION, PRODUCT WEIGHT, BARCODE SCANNING)" />
        </View>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Check points</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Sample Size</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Specification</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Actual Finding/Reading</Text></View>
          </View>
          {globalAudits.length > 0 ? (
            globalAudits.map((a, i) => {
              const ss = a.data?.sampleSize ?? a.data?.sample_size ?? NILL;
              const r = (a.result || NILL).toString().toUpperCase();
              return (
                <View key={a.id || i} style={s.dataBodyRow}>
                  <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{a.type}</Text></View>
                  <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{ss}</Text></View>
                  <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(a.data?.specification)}</Text></View>
                  <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{r}</Text></View>
                </View>
              );
            })
          ) : (
            <View style={s.dataBodyRow}>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            </View>
          )}
        </View>

        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Defect Code</Text></View>
            <View style={[s.dataCellLast, { flex: 3 }]}><Text style={s.dataCellTextHead}>Defect Description</Text></View>
          </View>
          <View style={s.dataBodyRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            <View style={[s.dataCellLast, { flex: 3 }]}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
          </View>
        </View>
        <View style={s.remarksBlock}>
          <Text style={s.remarksLabel}>Remarks:</Text>
          <Text style={{ fontSize: 9 }}>{NILL}</Text>
        </View>

        {/* Part 4 */}
        <Text style={s.sectionTitle}>Part4- Packaging and shipment quantity</Text>
        <View style={s.checkboxRow}>
          <Checkbox label="Conformed to Packaging" />
          <Checkbox label="Conformed to Quantity" />
        </View>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Defect Code</Text></View>
            <View style={[s.dataCellLast, { flex: 3 }]}><Text style={s.dataCellTextHead}>Defect Description</Text></View>
          </View>
          <View style={s.dataBodyRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            <View style={[s.dataCellLast, { flex: 3 }]}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
          </View>
        </View>
        <View style={s.remarksBlock}>
          <Text style={s.remarksLabel}>Informative Remarks:</Text>
          <Text style={{ fontSize: 9 }}>{NILL}</Text>
        </View>
        <View style={{ marginBottom: 6 }}>
          <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold' }}>6. Product packing and carton dimension detail as below:</Text>
          <Text style={{ fontSize: 9, marginLeft: 14, marginTop: 2 }}>1. POLYBAG MEASURMENT: {NILL}</Text>
          <Text style={{ fontSize: 9, marginLeft: 14 }}>2. CARTON DIMENSION: {NILL}</Text>
        </View>

        {/* Part 5 */}
        <Text style={s.sectionTitle}>Part5-Minimum requirements for inspection</Text>
        <View style={s.checkboxRow}>
          <Checkbox label="Reference/Shipment seal sample ready" />
          <Checkbox label="PO ready" />
          <Checkbox label="Product spec /inspection file" />
        </View>
        <View style={s.checkboxRow}>
          <Checkbox label="ready Random sampling allowed" />
          <Checkbox label="Shipment ready (100% produced and packed)" />
        </View>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Defect Code</Text></View>
            <View style={[s.dataCellLast, { flex: 3 }]}><Text style={s.dataCellTextHead}>Defect Description</Text></View>
          </View>
          <View style={s.dataBodyRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            <View style={[s.dataCellLast, { flex: 3 }]}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
          </View>
        </View>
        <View style={s.remarksBlock}>
          <Text style={s.remarksLabel}>Remarks:</Text>
          <Text style={{ fontSize: 9 }}>{NILL}</Text>
        </View>

        <PageFooter />
      </Page>

      {/* ═══ PAGE 4 — Supplementary ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop logoSrc={logoSrc} />

        <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', marginTop: 4, marginBottom: 6 }}>
          Supplementary–Carton Weight Check
        </Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Article No.</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Carton Number</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Declared Weight (Kg)</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Actual Weight (Kg)</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Remarks</Text></View>
          </View>
          {(() => {
            const weightAudits = globalAudits.filter(a => a.type === 'WEIGHT');
            if (weightAudits.length === 0) {
              return (
                <View style={s.dataBodyRow}>
                  <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{articleNo}</Text></View>
                  <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
                  <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
                  <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
                  <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
                </View>
              );
            }
            return weightAudits.map((a, i) => {
              const sku = skus.find(sk => (sk.po_sku_id || sk.id) === a.sku_id);
              const art = sku?.sku_code || sku?.sku_name || articleNo;
              const decl = a.data?.declaredWeight ?? a.data?.declared_weight ?? NILL;
              const act = a.data?.actualWeight ?? a.data?.actual_weight ?? NILL;
              return (
                <View key={a.id || i} style={s.dataBodyRow}>
                  <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{art}</Text></View>
                  <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(a.data?.cartonNumber)}</Text></View>
                  <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{decl}</Text></View>
                  <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{act}</Text></View>
                  <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{val(a.notes || a.remarks)}</Text></View>
                </View>
              );
            });
          })()}
        </View>

        <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', marginTop: 14, marginBottom: 6 }}>
          Supplementary– Carton Condition and Label Check
        </Text>
        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Article No.</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Carton Number</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Carton Visual{'\n'}Appearance{'\n'}(OK/NOTOK)</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Poly bag/Carton{'\n'}Label{'\n'}(Conformed/Not{'\n'}Conformed)</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Remarks</Text></View>
          </View>
          <View style={s.dataBodyRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{articleNo}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
          </View>
        </View>

        <View style={{ marginTop: 14 }}>
          <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold' }}>Picture Summary-List (Just for Examples)</Text>
          <View style={s.picList}>
            {[
              'Carton Photos',
              'Packing Photos',
              'Product Photos',
              'Labels',
              'On-site Test Photos',
              'Defect Photos',
              'Documents Photos',
              'Metal Detector and Needles Control Record',
              'Pull Test',
            ].map((item, i) => (
              <View key={item} style={s.picListItem}>
                <Text style={s.picListNum}>{i + 1}.</Text>
                <Text style={s.picListText}>{item}</Text>
              </View>
            ))}
          </View>
        </View>

        <PageFooter />
      </Page>

      {/* ═══ PAGE 5 — Measurement Report (digital table) ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop logoSrc={logoSrc} />
        <Text style={s.bigSectionTitle}>MESUREMENT REPORT</Text>

        {(() => {
          const measAudits = globalAudits.filter(a => a.type === 'MEASUREMENT');
          if (measAudits.length === 0) {
            return (
              <Text style={{ fontSize: 9, color: '#444', marginLeft: 14 }}>{NILL}</Text>
            );
          }
          return measAudits.map((a, idx) => {
            const sku = skus.find(sk => (sk.po_sku_id || sk.id) === a.sku_id);
            const art = sku?.sku_code || sku?.sku_name || articleNo;
            const d = a.data || {};
            const measurements = Array.isArray(d.measurements) ? d.measurements
              : Array.isArray(d.points) ? d.points
              : [];
            const auditResult = (a.result || 'pending').toString().toUpperCase();
            return (
              <View key={a.id || idx} style={{ marginBottom: 14 }}>
                <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', marginBottom: 6 }}>
                  Article No: {art}
                </Text>
                <View style={s.dataTable}>
                  <View style={s.dataHeadRow}>
                    <View style={[s.dataCell, { flex: 0.5 }]}><Text style={s.dataCellTextHead}>#</Text></View>
                    <View style={[s.dataCell, { flex: 2 }]}><Text style={s.dataCellTextHead}>Measurement</Text></View>
                    <View style={s.dataCell}><Text style={s.dataCellTextHead}>Tolerance</Text></View>
                    <View style={s.dataCell}><Text style={s.dataCellTextHead}>Specification (CM)</Text></View>
                    <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Actual (CM)</Text></View>
                  </View>
                  {measurements.length > 0 ? (
                    measurements.map((m: any, i: number) => (
                      <View key={i} style={s.dataBodyRow}>
                        <View style={[s.dataCell, { flex: 0.5 }]}><Text style={s.dataCellTextCenter}>{i + 1}</Text></View>
                        <View style={[s.dataCell, { flex: 2 }]}><Text style={s.dataCellText}>{val(m.name || m.label || m.measurement)}</Text></View>
                        <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(m.tolerance || m.tol)}</Text></View>
                        <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(m.specification || m.spec)}</Text></View>
                        <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{val(m.actual || m.reading)}</Text></View>
                      </View>
                    ))
                  ) : (
                    <View style={s.dataBodyRow}>
                      <View style={[s.dataCell, { flex: 0.5 }]}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
                      <View style={[s.dataCell, { flex: 2 }]}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
                      <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
                      <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
                      <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
                    </View>
                  )}
                </View>
                <Text style={{ fontSize: 9, marginLeft: 6, marginTop: 2 }}>
                  Status:{' '}
                  <Text style={{ fontFamily: 'Helvetica-Bold' }}>{auditResult}</Text>
                </Text>
              </View>
            );
          });
        })()}

        <PageFooter />
      </Page>

      {/* ═══ PAGE 6 — Packing List ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop logoSrc={logoSrc} />
        <Text style={s.bigSectionTitle}>PACKING LIST</Text>

        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>SHIPPER</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>CONSIGNEE</Text></View>
          </View>
          <View style={s.dataBodyRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(inspection.customer_name)}</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
          </View>
        </View>

        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>CONTRACT NO</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>ORDER NO</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>ARTICLE NO</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>INVOICE DATE</Text></View>
          </View>
          <View style={s.dataBodyRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(inspection.po_number)}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(inspection.po_number)}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{articleNo}</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{formatDate(inspection.po_ship_date)}</Text></View>
          </View>
        </View>

        <View style={s.dataTable}>
          <View style={s.dataHeadRow}>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>SKU / Article</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Colour</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Size</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Order Qty</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextHead}>Carton Dim</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextHead}>Weight (Kg)</Text></View>
          </View>
          {skus.length > 0 ? (
            skus.map((sku, i) => (
              <View key={sku.id || i} style={s.dataBodyRow}>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(sku.sku_code || sku.sku_name)}</Text></View>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(sku.colour || sku.color)}</Text></View>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{val(sku.size)}</Text></View>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{sku.quantity ?? sku.ordered_qty ?? NILL}</Text></View>
                <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
                <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              </View>
            ))
          ) : (
            <View style={s.dataBodyRow}>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
              <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            </View>
          )}
          <View style={[s.dataBodyRow, { backgroundColor: '#f5f5f5' }]}>
            <View style={[s.dataCell, { flex: 3 }]}><Text style={[s.dataCellTextBold, { textAlign: 'right' }]}>TOTAL</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{totalOrderQty}</Text></View>
            <View style={s.dataCell}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
            <View style={s.dataCellLast}><Text style={s.dataCellTextCenter}>{NILL}</Text></View>
          </View>
        </View>

        <View style={{ marginTop: 12, flexDirection: 'row', justifyContent: 'flex-end' }}>
          <View>
            <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold' }}>Signature & Date</Text>
            {inspection.inspector_signature_url ? (
              <Image src={inspection.inspector_signature_url} style={s.sigImage} />
            ) : null}
            <Text style={{ fontSize: 8.5, marginTop: 2 }}>{inspectorName}</Text>
            <Text style={{ fontSize: 8.5 }}>{formatDate(inspection.created_at)}</Text>
          </View>
        </View>

        <PageFooter />
      </Page>

      {/* ═══ PAGE 7+ — Photos ═══ */}
      {(() => {
        const allPhotos: { url: string; label?: string }[] = [];
        for (const p of globalPhotos) {
          if (p.image_url) allPhotos.push({ url: p.image_url, label: p.label || p.tile_label || p.tile_key || '' });
        }
        for (const sku of skus) {
          for (const arr of [sku.photoTiles, sku.photo_tiles, sku.photos]) {
            for (const p of arr || []) {
              if (p.image_url) allPhotos.push({ url: p.image_url, label: p.label || p.tile_label || p.tile_key || '' });
            }
          }
        }
        for (const d of globalDefects) {
          for (const src of d.evidence_images || []) {
            if (src) allPhotos.push({ url: src, label: `Defect: ${d.title || d.type || ''}` });
          }
        }
        for (const sku of skus) {
          for (const d of sku.defects || []) {
            for (const src of d.evidence_images || []) {
              if (src) allPhotos.push({ url: src, label: `Defect: ${d.title || d.type || ''}` });
            }
          }
        }

        if (allPhotos.length === 0) {
          return (
            <Page size="A4" style={s.page}>
              <PageTop logoSrc={logoSrc} />
              <Text style={s.bigSectionTitle}>PICTURES</Text>
              <Text style={{ fontSize: 9, marginLeft: 18, color: '#444' }}>{NILL}</Text>
              <PageFooter />
            </Page>
          );
        }

        // Chunk into pages of 6 (2 cols × 3 rows)
        const pages: { url: string; label?: string }[][] = [];
        for (let i = 0; i < allPhotos.length; i += 6) {
          pages.push(allPhotos.slice(i, i + 6));
        }
        return pages.map((chunk, pi) => (
          <Page key={pi} size="A4" style={s.page}>
            <PageTop logoSrc={logoSrc} />
            {pi === 0 ? <Text style={s.bigSectionTitle}>PICTURES</Text> : null}
            <View style={s.photoGrid}>
              {chunk.map((ph, i) => (
                <View key={i} style={s.photoTile}>
                  <Image src={ph.url} style={s.photoImg} />
                  {ph.label ? <Text style={s.photoCaption}>{ph.label}</Text> : null}
                </View>
              ))}
            </View>
            <PageFooter />
          </Page>
        ));
      })()}

    </Document>
  );
};

export default MigrosFinalInspectionReportPDF;
